(() => {
  if (!window.customElements || window.customElements.get('ws-promo-banner')) return;

  const DEFAULT_DISMISS_SECONDS = 7200;
  const MAX_DISMISS_SECONDS = 31536000;
  const MAX_TIMER_DELAY = 2147483647;
  const POLL_INTERVAL = 30000;
  const NUMERIC_ID = /^\d+$/;
  const SUPPORTED_LOCALES = new Set(['it', 'en', 'fr', 'de', 'es']);
  const CONTROL_SELECTOR = [
    '.sticky-atc-bar',
    '.mobile-sticky-bar'
  ].join(',');

  function chatLauncher() {
    return document.getElementById('voiceflow-chat')?.shadowRoot?.querySelector('.vfrc-launcher');
  }

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function numberId(value, allowNumber) {
    if (typeof value === 'string' && NUMERIC_ID.test(value)) return value;
    if (allowNumber && typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) return String(value);
    return null;
  }

  function integer(value, minimum) {
    return Number.isSafeInteger(value) && value >= minimum ? value : null;
  }

  function time(value) {
    if (typeof value !== 'string' || value.length < 20) return NaN;
    const result = Date.parse(value);
    return Number.isFinite(result) ? result : NaN;
  }


  function normalizeVariant(value, allowNumberId) {
    if (!isObject(value)) return null;

    const id = numberId(value.id, allowNumberId);
    const price = integer(value.price, 0);
    const compareValue = Object.prototype.hasOwnProperty.call(value, 'compareAtPrice') ? value.compareAtPrice : value.compare_at_price;
    const compareAtPrice = compareValue === null || compareValue === undefined ? null : integer(compareValue, 0);

    if (!id || price === null || (compareValue !== null && compareValue !== undefined && compareAtPrice === null)) return null;
    if (typeof value.available !== 'boolean') return null;

    return { id, price, compareAtPrice, available: value.available };
  }

  function normalizeContext(value) {
    if (!isObject(value)) return null;

    const productId = numberId(value.productId, false);
    const marketId = numberId(value.marketId, false);
    const locale = typeof value.locale === 'string' ? value.locale.toLowerCase() : '';
    const currency = typeof value.currency === 'string' ? value.currency.toUpperCase() : '';
    const variant = normalizeVariant(value.variant, false);

    if (!productId || !marketId || !SUPPORTED_LOCALES.has(locale) || !/^[A-Z]{3}$/.test(currency) || typeof value.b2b !== 'boolean') {
      return null;
    }

    return { productId, marketId, locale, currency, b2b: value.b2b, variant };
  }

  function normalizeDismiss(value) {
    if (value === null || value === undefined) {
      return { mode: 'interval', seconds: DEFAULT_DISMISS_SECONDS };
    }
    if (!isObject(value) || (value.mode !== 'session' && value.mode !== 'interval')) return null;
    const seconds = integer(value.seconds, 1);
    if (seconds === null || seconds > MAX_DISMISS_SECONDS) return null;
    return { mode: value.mode, seconds };
  }

  function normalizeCta(value) {
    if (value === null) return null;
    if (!isObject(value) || typeof value.label !== 'string' || typeof value.url !== 'string') return undefined;

    const label = value.label.trim();
    const url = value.url.trim();
    if (!label || label.length > 200 || !url || url.length > 2048) return undefined;
    return { label, url };
  }

  function normalizeEntry(value) {
    if (!isObject(value)) return null;
    if (typeof value.bannerId !== 'string' || !value.bannerId || typeof value.promotionId !== 'string' || !value.promotionId) return null;
    if (value.promotionType !== 'price' && value.promotionType !== 'discount') return null;
    if (value.kind !== 'price' && value.kind !== 'automatic' && value.kind !== 'code') return null;
    if ((value.kind === 'price') !== (value.promotionType === 'price')) return null;

    const priority = integer(value.priority, -Number.MAX_SAFE_INTEGER);
    const marketId = numberId(value.marketId, false);
    const locale = typeof value.locale === 'string' ? value.locale.toLowerCase() : '';
    const currency = value.currency === null ? null : typeof value.currency === 'string' ? value.currency.toUpperCase() : undefined;
    const startsAt = time(value.startsAt);
    const endsAt = value.endsAt === null ? null : time(value.endsAt);
    const validUntil = time(value.validUntil);
    const dismiss = normalizeDismiss(value.dismiss);
    const cta = normalizeCta(value.cta);

    if (
      priority === null ||
      !marketId ||
      !SUPPORTED_LOCALES.has(locale) ||
      currency === undefined ||
      (currency !== null && !/^[A-Z]{3}$/.test(currency)) ||
      !Number.isFinite(startsAt) ||
      (endsAt !== null && !Number.isFinite(endsAt)) ||
      !Number.isFinite(validUntil) ||
      (endsAt !== null && endsAt < startsAt) ||
      validUntil < startsAt ||
      typeof value.text !== 'string' ||
      !value.text.trim() ||
      Array.from(value.text).length > 400 ||
      !dismiss ||
      cta === undefined
    ) {
      return null;
    }

    let variantIds = null;
    if (value.variantIds !== null) {
      if (!Array.isArray(value.variantIds)) return null;
      variantIds = [];
      for (const variantId of value.variantIds) {
        const id = numberId(variantId, false);
        if (!id) return null;
        variantIds.push(id);
      }
    }

    let priceGuard = null;
    if (value.kind === 'price') {
      if (!isObject(value.priceGuard) || currency !== 'EUR') return null;
      const price = integer(value.priceGuard.price, 0);
      const compareAtPrice = integer(value.priceGuard.compareAtPrice, 0);
      if (price === null || compareAtPrice === null || compareAtPrice <= price) return null;
      priceGuard = { price, compareAtPrice };
    } else if (value.priceGuard !== null) {
      return null;
    }

    return {
      bannerId: value.bannerId,
      promotionType: value.promotionType,
      promotionId: value.promotionId,
      priority,
      kind: value.kind,
      marketId,
      variantIds,
      locale,
      currency,
      startsAt,
      endsAt,
      validUntil,
      text: value.text,
      cta,
      dismiss,
      priceGuard
    };
  }

  function normalizePayload(value, context) {
    if (!isObject(value) || value.schemaVersion !== 1 || numberId(value.productId, false) !== context.productId || !Array.isArray(value.entries)) {
      return null;
    }

    const generatedAt = time(value.generatedAt);
    const validUntil = time(value.validUntil);
    if (!Number.isFinite(generatedAt) || !Number.isFinite(validUntil) || generatedAt > validUntil) return null;

    const entries = [];
    for (const rawEntry of value.entries) {
      const entry = normalizeEntry(rawEntry);
      if (!entry) return null;
      entries.push(entry);
    }

    return { generatedAt, validUntil, entries };
  }

  function normalizeEnvelope(value) {
    if (!isObject(value)) return null;
    const context = normalizeContext(value.context);
    if (!context) return null;
    if (value.payload === null) return { context, payload: null };

    const payload = normalizePayload(value.payload, context);
    return payload ? { context, payload } : null;
  }

  function visibleRect(element) {
    if (!(element instanceof Element) || element.hidden) return null;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number.parseFloat(style.opacity) === 0) return null;

    const rect = element.getBoundingClientRect();
    const left = Math.max(0, rect.left);
    const top = Math.max(0, rect.top);
    const right = Math.min(window.innerWidth, rect.right);
    const bottom = Math.min(window.innerHeight, rect.bottom);
    if (right <= left || bottom <= top) return null;

    return { left, top, right, bottom, width: right - left, height: bottom - top };
  }

  function intersects(first, second) {
    return first.left < second.right && first.right > second.left && first.top < second.bottom && first.bottom > second.top;
  }

  function cookieName(bannerId) {
    return `ws_promo_banner_${encodeURIComponent(bannerId).replace(/%/g, '_')}`;
  }

  function readCookie(name) {
    try {
      const prefix = `${name}=`;
      for (const part of document.cookie.split(';')) {
        const value = part.trim();
        if (value.startsWith(prefix)) return decodeURIComponent(value.slice(prefix.length));
      }
    } catch (_) {
      // Storage privacy restrictions are a normal fallback path.
    }
    return null;
  }

  function writeCookie(name, value, seconds) {
    try {
      const maxAge = Number.isFinite(seconds) ? `; max-age=${Math.ceil(seconds)}` : '';
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/; samesite=lax${maxAge}`;
      return true;
    } catch (_) {
      return false;
    }
  }

  function clearCookie(name) {
    try {
      document.cookie = `${name}=; path=/; max-age=0; samesite=lax`;
    } catch (_) {
      // Nothing else can be done when cookies are unavailable.
    }
  }

  function safeCta(value) {
    if (!value) return null;
    if (/[\x00-\x1f\x7f]/.test(value.url) || value.url.startsWith('//')) return null;

    try {
      const destination = new URL(value.url, window.location.origin);
      const sameOrigin = destination.origin === window.location.origin;
      if ((!sameOrigin && destination.protocol !== 'https:') || destination.username || destination.password) return null;

      const pathParts = destination.pathname.split('/').filter(Boolean);
      const cartIndex = pathParts.indexOf('cart');
      if (sameOrigin && cartIndex !== -1 && /^add(?:\.js)?$/i.test(pathParts[cartIndex + 1] || '')) return null;

      return { label: value.label, href: destination.href };
    } catch (_) {
      return null;
    }
  }

  class WsPromoBanner extends HTMLElement {
    constructor() {
      super();
      this.context = null;
      this.payload = null;
      this.winner = null;
      this.productSectionId = null;
      this.started = false;
      this.runtimeBound = false;
      this.relocating = false;
      this.requestId = 0;
      this.lastPath = window.location.pathname;
      this.lastProductRefreshAt = 0;
      this.pendingProductKey = null;
      this.sessionDismissals = new Map();
      this.intervalDismissals = new Map();
      this.controlNodes = new Set();
      this.consentReady = false;
      this.consentGateStarted = false;
      this.consentGateChecked = false;
      this.pubsubAttempts = 0;

      this.handleClose = this.handleClose.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleResizeOrScroll = this.handleResizeOrScroll.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
      this.handleConsent = this.handleConsent.bind(this);
      this.handleFreshVariant = this.handleFreshVariant.bind(this);
    }

    connectedCallback() {
      const duplicate = Array.from(document.querySelectorAll('ws-promo-banner')).find((element) => element !== this && element.id === this.id);
      if (duplicate) duplicate.remove();

      if (this.parentElement !== document.body) {
        this.relocating = true;
        document.body.append(this);
        return;
      }

      this.relocating = false;
      if (this.started) return;
      this.started = true;

      this.textElement = this.querySelector('[data-ws-promo-text]');
      this.linkElement = this.querySelector('[data-ws-promo-link]');
      this.closeButton = this.querySelector('[data-ws-promo-close]');
      if (!this.textElement || !this.linkElement || !this.closeButton) {
        this.hide();
        return;
      }

      this.closeButton.addEventListener('click', this.handleClose);
      const envelope = normalizeEnvelope(this.readEnvelope(this));
      if (envelope) this.applyEnvelope(envelope);
      else this.hide();

      this.domReadyHandler = () => this.bindRuntime();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.domReadyHandler, { once: true });
      } else {
        this.bindRuntime();
      }
    }

    disconnectedCallback() {
      if (this.relocating) return;
      this.stop();
    }

    stop() {
      document.removeEventListener('DOMContentLoaded', this.domReadyHandler);
      this.closeButton?.removeEventListener('click', this.handleClose);
      this.variantUnsubscribe?.();
      this.variantUnsubscribe = null;
      window.clearTimeout(this.pubsubRetry);
      window.clearTimeout(this.pollTimer);
      window.clearTimeout(this.expiryTimer);
      window.clearTimeout(this.dismissTimer);
      window.clearTimeout(this.consentDelay);
      if (this.consentLoadHandler) window.removeEventListener('load', this.consentLoadHandler);
      this.requestId += 1;
      this.fetchController?.abort();
      this.fetchController = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.mutationObserver?.disconnect();
      this.mutationObserver = null;
      window.removeEventListener('resize', this.handleResizeOrScroll);
      window.removeEventListener('scroll', this.handleResizeOrScroll);
      window.removeEventListener('storage', this.handleResizeOrScroll);
      window.removeEventListener('pageshow', this.handlePageShow);
      window.removeEventListener('popstate', this.handlePopState);
      document.removeEventListener('scroll', this.handleResizeOrScroll, true);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('visitorConsentCollected', this.handleConsent);
      if (this.placementFrame) window.cancelAnimationFrame(this.placementFrame);
      if (this.evaluationFrame) window.cancelAnimationFrame(this.evaluationFrame);
      if (this.productFrame) window.cancelAnimationFrame(this.productFrame);
      if (this.refreshFrame) window.cancelAnimationFrame(this.refreshFrame);
      this.placementFrame = 0;
      this.evaluationFrame = 0;
      this.productFrame = 0;
      this.refreshFrame = 0;
      this.controlNodes.clear();
      this.consentReady = false;
      this.consentGateStarted = false;
      this.consentGateChecked = false;
      this.pubsubAttempts = 0;
      this.started = false;
      this.runtimeBound = false;
    }

    bindRuntime() {
      if (!this.isConnected || this.runtimeBound) return;
      this.runtimeBound = true;

      window.addEventListener('resize', this.handleResizeOrScroll, { passive: true });
      window.addEventListener('scroll', this.handleResizeOrScroll, { passive: true });
      window.addEventListener('storage', this.handleResizeOrScroll);
      window.addEventListener('pageshow', this.handlePageShow);
      window.addEventListener('popstate', this.handlePopState);
      document.addEventListener('scroll', this.handleResizeOrScroll, true);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('visitorConsentCollected', this.handleConsent);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.requestEvaluation());
        this.resizeObserver.observe(document.documentElement);
        this.resizeObserver.observe(this);
      }
      this.mutationObserver = new MutationObserver(this.handleMutations);
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-modal']
      });

      this.observeControls();
      this.findProductSection();
      this.subscribeToFreshVariants();
      this.beginConsentGate();
      this.requestEvaluation();
      this.refreshSection('initial');
      this.schedulePoll();
    }

    readEnvelope(source) {
      const node = source?.querySelector?.('script[data-ws-promo-data]');
      if (!node || !node.textContent) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (_) {
        return null;
      }
    }

    applyEnvelope(envelope) {
      this.context = envelope.context;
      this.payload = envelope.payload;
      this.lastPath = window.location.pathname;
      this.pendingProductKey = null;
      this.findProductSection();
      this.evaluate();
    }

    findProductSection() {
      const productInfo = document.querySelector('#MainContent product-info[data-product-id]') || document.querySelector('product-info[data-product-id]');
      if (!productInfo) {
        this.productSectionId = null;
        return null;
      }
      this.productSectionId = productInfo.dataset.section || null;
      return productInfo;
    }

    subscribeToFreshVariants() {
      if (this.variantUnsubscribe || !this.isConnected) return;
      const pubsub = window.FoxTheme?.pubsub;
      if (pubsub?.subscribe && pubsub.PUB_SUB_EVENTS?.variantChange) {
        this.variantUnsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, this.handleFreshVariant);
        return;
      }
      if (this.pubsubAttempts >= 50) return;
      this.pubsubAttempts += 1;
      this.pubsubRetry = window.setTimeout(() => this.subscribeToFreshVariants(), 100);
    }
    beginConsentGate() {
      if (this.consentGateStarted) return;
      this.consentGateStarted = true;

      const check = () => {
        this.consentLoadHandler = null;
        this.consentDelay = window.setTimeout(() => {
          this.consentGateChecked = true;
          this.maybeReleaseConsentGate();
        }, 800);
      };

      if (document.readyState === 'complete') check();
      else {
        this.consentLoadHandler = check;
        window.addEventListener('load', check, { once: true });
      }
    }

    maybeReleaseConsentGate() {
      if (this.consentReady || !this.consentGateChecked || visibleRect(document.getElementById('shopify-pc__banner'))) return;
      this.consentReady = true;
      this.requestEvaluation();
    }


    handleFreshVariant(event) {
      if (!this.context || !event?.data) return;
      const eventSection = event.data.sectionId;
      if (this.productSectionId && eventSection !== this.productSectionId) return;

      if (!this.productSectionId && event.data.html) {
        const productInfo = event.data.html.querySelector?.('product-info[data-product-id]');
        const productId = numberId(productInfo?.dataset?.productId, true);
        if (productId && productId !== this.context.productId) return;
      }

      const variant = normalizeVariant(event.data.variant, true);
      this.context = { ...this.context, variant };
      this.evaluate();
      this.queueRefresh('variant');
    }

    handleClose() {
      if (!this.winner) return;
      this.storeDismissal(this.winner);
      this.evaluate();
    }

    handleVisibilityChange() {
      if (document.visibilityState !== 'visible') {
        window.clearTimeout(this.pollTimer);
        return;
      }
      this.evaluate();
      this.queueRefresh('visibility');
      this.schedulePoll();
    }

    handlePageShow() {
      this.evaluate();
      this.queueRefresh('visibility');
    }

    handlePopState() {
      this.requestProductCheck();
      this.queueRefresh('product-swap');
    }

    handleConsent() {
      this.consentGateChecked = true;
      this.consentReady = true;
      window.clearTimeout(this.consentDelay);
      this.requestEvaluation();
      this.queueRefresh('visibility');
    }

    handleResizeOrScroll() {
      this.requestEvaluation();
    }

    handleMutations(records) {
      if (records.every((record) => record.target === this || this.contains(record.target))) return;
      this.maybeReleaseConsentGate();
      this.observeControls();
      this.requestEvaluation();
      this.requestProductCheck();
    }

    requestEvaluation() {
      if (!this.isConnected || this.evaluationFrame) return;
      this.evaluationFrame = window.requestAnimationFrame(() => {
        this.evaluationFrame = 0;
        this.evaluate();
      });
    }

    requestProductCheck() {
      if (!this.isConnected || this.productFrame) return;
      this.productFrame = window.requestAnimationFrame(() => {
        this.productFrame = 0;
        this.checkProductSwap();
      });
    }

    checkProductSwap() {
      const productInfo = this.findProductSection();
      const productId = numberId(productInfo?.dataset?.productId, true);
      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      const pathChanged = window.location.pathname !== this.lastPath;
      const productChanged = Boolean(productId && this.context && productId !== this.context.productId);
      const variantChanged = Boolean(urlVariant && this.context?.variant && urlVariant !== this.context.variant.id);
      if (!productChanged && !pathChanged && !variantChanged) return;

      const key = `${productId || ''}:${window.location.pathname}:${urlVariant || ''}`;
      const now = Date.now();
      if (this.pendingProductKey === key && now - this.lastProductRefreshAt < 1000) return;
      this.pendingProductKey = key;
      this.lastProductRefreshAt = now;
      if (productChanged || pathChanged) {
        this.context = null;
        this.payload = null;
      } else if (variantChanged) this.context = { ...this.context, variant: null };
      this.hide();
      this.queueRefresh('product-swap');
    }

    queueRefresh(reason) {
      if (!this.isConnected) return;
      // Invalidate in-flight work now, not in the next animation frame.
      // Otherwise an old response can overwrite a newly selected variant.
      this.requestId += 1;
      this.fetchController?.abort();
      this.fetchController = null;
      this.refreshReason = reason;
      if (this.refreshFrame) return;
      this.refreshFrame = window.requestAnimationFrame(() => {
        this.refreshFrame = 0;
        this.refreshSection(this.refreshReason);
      });
    }

    async refreshSection(reason) {
      if (!this.isConnected) return;
      const urgent = reason === 'variant' || reason === 'product-swap' || reason === 'visibility' || reason === 'expiry';
      if (this.fetchController) {
        if (!urgent) return;
        this.fetchController.abort();
      }

      const requestId = ++this.requestId;
      const controller = new AbortController();
      this.fetchController = controller;

      try {
        const url = new URL(window.location.href);
        const variantId = this.context?.variant?.id;
        if (reason === 'variant' && variantId) url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url.toString(), {
          signal: controller.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(`Section response ${response.status}`);

        const markup = await response.text();
        if (!markup.trim()) throw new Error('Empty section response');
        const documentResponse = new DOMParser().parseFromString(markup, 'text/html');
        const source = documentResponse.getElementById(this.id) || Array.from(documentResponse.querySelectorAll('ws-promo-banner')).find(
          (element) => element.dataset.sectionId === this.dataset.sectionId
        );
        const envelope = normalizeEnvelope(this.readEnvelope(source));
        if (requestId !== this.requestId || !this.isConnected) return;
        if (!envelope) {
          this.payload = null;
          this.hide();
          return;
        }
        this.applyEnvelope(envelope);
      } catch (error) {
        if (error?.name === 'AbortError' || requestId !== this.requestId || !this.isConnected) return;
        this.payload = null;
        this.hide();
      } finally {
        if (requestId === this.requestId) this.fetchController = null;
      }
    }

    schedulePoll() {
      window.clearTimeout(this.pollTimer);
      if (!this.runtimeBound || document.visibilityState !== 'visible') return;
      this.pollTimer = window.setTimeout(async () => {
        this.pollTimer = 0;
        await this.refreshSection('poll');
        this.schedulePoll();
      }, POLL_INTERVAL);
    }

    evaluate() {
      const winner = this.findWinner();
      this.winner = winner;
      this.scheduleExpiry();

      if (!this.consentReady || !winner || this.hasBlockingOverlay()) {
        this.scheduleDismissCheck(null);
        this.hide();
        return;
      }

      const dismissal = this.dismissalState(winner);
      this.scheduleDismissCheck(dismissal);
      if (dismissal.dismissed) {
        this.hide();
        return;
      }

      this.render(winner);
      if (this.hidden) this.hidden = false;
      this.requestPlacement();
    }

    findWinner() {
      if (!this.context || !this.payload || this.context.b2b) return null;
      const now = Date.now();
      if (now >= this.payload.validUntil || !this.context.variant?.available) return null;

      const entries = this.payload.entries.filter((entry) => this.entryIsEligible(entry, now));
      entries.sort((first, second) => second.priority - first.priority || first.bannerId.localeCompare(second.bannerId));
      return entries[0] || null;
    }

    entryIsEligible(entry, now) {
      if (now < entry.startsAt || now >= entry.validUntil || (entry.endsAt !== null && now >= entry.endsAt)) return false;
      if (entry.marketId !== this.context.marketId || entry.locale !== this.context.locale) return false;
      if (entry.currency !== null && entry.currency !== this.context.currency) return false;
      if (entry.variantIds !== null && !entry.variantIds.includes(this.context.variant.id)) return false;

      if (entry.kind === 'price') {
        const guard = entry.priceGuard;
        if (!guard || entry.currency !== 'EUR' || this.context.currency !== 'EUR') return false;
        if (this.context.variant.price !== guard.price || this.context.variant.compareAtPrice !== guard.compareAtPrice) return false;
      }

      return true;
    }

    render(entry) {
      const renderedKey = `${entry.bannerId}\u0000${entry.text}\u0000${entry.cta?.label || ''}\u0000${entry.cta?.url || ''}`;
      if (this.renderedKey === renderedKey) return;

      this.textElement.textContent = entry.text;
      const cta = safeCta(entry.cta);
      if (cta) {
        this.linkElement.textContent = cta.label;
        this.linkElement.href = cta.href;
        this.linkElement.hidden = false;
      } else {
        this.linkElement.textContent = '';
        this.linkElement.removeAttribute('href');
        this.linkElement.hidden = true;
      }
      this.renderedKey = renderedKey;
    }

    hide() {
      if (this.classList.contains('is-visible')) this.classList.remove('is-visible');
      if (!this.hidden) this.hidden = true;
    }

    requestPlacement() {
      if (!this.isConnected || this.placementFrame) return;
      this.placementFrame = window.requestAnimationFrame(() => {
        this.placementFrame = 0;
        this.place();
      });
    }

    place() {
      if (!this.winner || this.hidden || this.hasBlockingOverlay()) {
        if (this.hasBlockingOverlay()) this.hide();
        return;
      }

      const banner = this.getBoundingClientRect();
      if (!banner.width || !banner.height) {
        this.hide();
        return;
      }

      const placement = this.safePlacement(banner.width, banner.height);
      if (!placement) {
        this.hide();
        return;
      }

      const left = `${Math.round(placement.left)}px`;
      const top = `${Math.round(placement.top)}px`;
      if (this.style.getPropertyValue('--ws-promo-left') !== left) this.style.setProperty('--ws-promo-left', left);
      if (this.style.getPropertyValue('--ws-promo-top') !== top) this.style.setProperty('--ws-promo-top', top);
      if (!this.classList.contains('is-visible')) this.classList.add('is-visible');
    }

    safePlacement(width, height) {
      const edge = Math.max(8, Number.parseInt(this.dataset.edgeSpacing || '20', 10) || 20);
      const gap = Math.max(8, Number.parseInt(this.dataset.controlGap || '16', 10) || 16);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      if (width > viewportWidth - edge * 2 || height > viewportHeight - edge * 2) return null;

      const controls = this.controlRects();
      let top;
      if (window.matchMedia('(max-width: 767px)').matches) {
        const bottomControls = controls.filter((rect) => rect.bottom >= viewportHeight - edge);
        const ceiling = bottomControls.length ? Math.min(...bottomControls.map((rect) => rect.top)) : viewportHeight;
        top = ceiling - height - gap;
      } else {
        top = viewportHeight - height - edge;
      }

      const left = Math.round((viewportWidth - width) / 2);
      for (let attempt = 0; attempt <= controls.length; attempt += 1) {
        const candidate = { left, top, right: left + width, bottom: top + height };
        if (candidate.left < edge || candidate.right > viewportWidth - edge || candidate.top < edge || candidate.bottom > viewportHeight - edge) return null;

        const collisions = controls.filter((rect) => intersects(candidate, rect));
        if (!collisions.length) return candidate;
        top = Math.min(...collisions.map((rect) => rect.top)) - height - gap;
      }

      return null;
    }

    controlRects() {
      const nodes = new Set();
      document.querySelectorAll(CONTROL_SELECTOR).forEach((node) => {
        if (node !== this) nodes.add(node);
      });
      const launcher = chatLauncher();
      if (launcher) nodes.add(launcher);
      return Array.from(nodes, visibleRect).filter(Boolean);
    }

    observeControls() {
      if (!this.resizeObserver) return;
      const nodes = [...document.querySelectorAll(`${CONTROL_SELECTOR}, #shopify-pc__banner, cart-drawer`)];
      const launcher = chatLauncher();
      if (launcher) nodes.push(launcher);
      nodes.forEach((node) => {
        if (this.controlNodes.has(node)) return;
        this.controlNodes.add(node);
        this.resizeObserver.observe(node);
      });
    }

    hasBlockingOverlay() {
      if (visibleRect(document.getElementById('shopify-pc__banner'))) return true;
      if (document.body.classList.contains('ws-chat-open')) return true;
      if (document.body.classList.contains('modal-show') || document.body.classList.contains('modal-showing')) return true;
      if (Array.from(document.querySelectorAll('cart-drawer[open], [id^="CartDrawer"][open], .cart-drawer[open]')).some((node) => visibleRect(node))) return true;

      const viewportArea = window.innerWidth * window.innerHeight;
      return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], modal-component[open], drawer-component[open], basic-modal[open]')).some((node) => {
        const rect = visibleRect(node);
        return rect && rect.width * rect.height >= viewportArea * 0.42;
      });
    }

    scheduleExpiry() {
      window.clearTimeout(this.expiryTimer);
      if (!this.payload) return;

      const now = Date.now();
      const deadlines = [this.payload.validUntil];
      for (const entry of this.payload.entries) {
        if (entry.startsAt > now) deadlines.push(entry.startsAt);
        if (entry.validUntil > now) deadlines.push(entry.validUntil);
        if (entry.endsAt !== null && entry.endsAt > now) deadlines.push(entry.endsAt);
      }

      const deadline = Math.min(...deadlines.filter((value) => value > now));
      if (!Number.isFinite(deadline)) return;
      const delay = deadline - now + 20;
      this.expiryTimer = window.setTimeout(() => {
        this.evaluate();
        this.queueRefresh('expiry');
      }, Math.min(delay, MAX_TIMER_DELAY));
    }

    dismissalState(entry) {
      const key = entry.bannerId;
      if (entry.dismiss.mode === 'session') {
        if (this.sessionDismissals.get(key) === '1') return { dismissed: true, until: null };
        const stored = this.readStorage(this.storage('sessionStorage'), key);
        if (stored === '1' || readCookie(cookieName(key)) === '1') return { dismissed: true, until: null };
        return { dismissed: false, until: null };
      }

      const now = Date.now();
      const candidates = [
        this.intervalDismissals.get(key),
        this.readTimestamp(this.readStorage(this.storage('localStorage'), key)),
        this.readTimestamp(readCookie(cookieName(key)))
      ];
      const until = Math.max(0, ...candidates.map((value) => (Number.isFinite(value) ? value : 0)));
      if (until > now) return { dismissed: true, until };

      this.intervalDismissals.delete(key);
      this.removeStorage(this.storage('localStorage'), key);
      clearCookie(cookieName(key));
      return { dismissed: false, until: null };
    }

    storeDismissal(entry) {
      const key = entry.bannerId;
      if (entry.dismiss.mode === 'session') {
        this.sessionDismissals.set(key, '1');
        this.writeStorage(this.storage('sessionStorage'), key, '1');
        writeCookie(cookieName(key), '1');
        return;
      }

      const until = Date.now() + entry.dismiss.seconds * 1000;
      this.intervalDismissals.set(key, until);
      if (!this.writeStorage(this.storage('localStorage'), key, String(until))) {
        writeCookie(cookieName(key), String(until), entry.dismiss.seconds);
      }
    }

    scheduleDismissCheck(dismissal) {
      window.clearTimeout(this.dismissTimer);
      if (!dismissal?.dismissed || !dismissal.until) return;
      const delay = dismissal.until - Date.now() + 20;
      this.dismissTimer = window.setTimeout(() => this.evaluate(), Math.min(Math.max(delay, 0), MAX_TIMER_DELAY));
    }

    storage(name) {
      try {
        return window[name];
      } catch (_) {
        return null;
      }
    }

    readStorage(storage, key) {
      try {
        return storage?.getItem(key) || null;
      } catch (_) {
        return null;
      }
    }

    writeStorage(storage, key, value) {
      if (!storage) return false;
      try {
        storage.setItem(key, value);
        return true;
      } catch (_) {
        return false;
      }
    }

    removeStorage(storage, key) {
      try {
        storage?.removeItem(key);
      } catch (_) {
        // Cookie and in-memory fallbacks are handled by the caller.
      }
    }

    readTimestamp(value) {
      if (typeof value !== 'string' || !/^\d+$/.test(value)) return null;
      const timestamp = Number(value);
      return Number.isSafeInteger(timestamp) ? timestamp : null;
    }
  }

  window.customElements.define('ws-promo-banner', WsPromoBanner);
})();
